import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, 
  Sparkles, 
  Undo2, 
  Redo2, 
  MessageSquare,
  Lightbulb,
  Shield,
  Zap,
  FileText,
  CheckCircle,
  User,
  Bot
} from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { cn } from '../lib/utils.ts';
import { ChatMessage, StoryboardStep } from '../types/index.ts';

interface EnhancedChatAssistantProps {
  activeStep: StoryboardStep | null;
  onStepUpdate: (stepId: string, updates: Partial<StoryboardStep>) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isLoading?: boolean;
}

const predefinedPrompts = [
  {
    id: 'clarity',
    icon: MessageSquare,
    text: 'Make this step clearer',
    description: 'Improve clarity and readability',
    color: 'bg-blue-100 text-blue-700 hover:bg-blue-200'
  },
  {
    id: 'safety',
    icon: Shield,
    text: 'Add safety warning',
    description: 'Include safety considerations',
    color: 'bg-red-100 text-red-700 hover:bg-red-200'
  },
  {
    id: 'simplify',
    icon: Zap,
    text: 'Simplify language',
    description: 'Make it more accessible',
    color: 'bg-green-100 text-green-700 hover:bg-green-200'
  },
  {
    id: 'detail',
    icon: FileText,
    text: 'Add more detail',
    description: 'Provide additional context',
    color: 'bg-purple-100 text-purple-700 hover:bg-purple-200'
  },
  {
    id: 'accuracy',
    icon: CheckCircle,
    text: 'Check for accuracy',
    description: 'Verify technical accuracy',
    color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
  }
];

const ChatBubble: React.FC<{
  message: ChatMessage;
  isUser: boolean;
}> = ({ message, isUser }) => {
  return (
    <div className={cn(
      'flex gap-3 mb-4',
      isUser ? 'flex-row-reverse' : 'flex-row'
    )}>
      <div className={cn(
        'flex items-center justify-center w-8 h-8 rounded-full',
        isUser ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
      )}>
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      
      <div className={cn(
        'max-w-[70%] p-3 rounded-lg',
        isUser 
          ? 'bg-primary text-primary-foreground' 
          : 'bg-secondary text-secondary-foreground'
      )}>
        <p className="text-sm">{message.content}</p>
        <div className="flex items-center gap-2 mt-2 text-xs opacity-70">
          <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
          {message.confidence && (
            <span className="flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              {Math.round(message.confidence * 100)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const EnhancedChatAssistant: React.FC<EnhancedChatAssistantProps> = ({
  activeStep,
  onStepUpdate,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  isLoading = false
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (activeStep) {
      const welcomeMessage: ChatMessage = {
        id: `welcome_${Date.now()}`,
        role: 'assistant',
        content: `I'm here to help you refine "${activeStep.onscreenText}". What would you like to improve?`,
        timestamp: new Date().toISOString(),
        confidence: 0.95
      };
      setMessages([welcomeMessage]);
    }
  }, [activeStep]);

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || !activeStep) return;

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsProcessing(true);

    try {
      // Simulate AI processing
      await new Promise(resolve => setTimeout(resolve, 1500));

      const aiResponse: ChatMessage = {
        id: `ai_${Date.now()}`,
        role: 'assistant',
              content: generateMockAIResponse(content, activeStep),
      timestamp: new Date().toISOString(),
      confidence: 0.85
    };

      setMessages(prev => [...prev, aiResponse]);

      // Simulate step update
      const updatedStep = generateStepUpdates(content, activeStep);
      if (updatedStep) {
        onStepUpdate(activeStep.id, updatedStep);
      }
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        confidence: 0
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePredefinedPrompt = (promptText: string) => {
    handleSendMessage(promptText);
  };

  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(inputValue);
  };

  const generateMockAIResponse = (prompt: string, step: StoryboardStep): string => {
    if (prompt.toLowerCase().includes('clear')) {
      return 'I\'ve made the step clearer by simplifying the language and breaking it into smaller actions.';
    }
    if (prompt.toLowerCase().includes('safety')) {
      return 'I\'ve added important safety warnings and precautions to ensure user safety.';
    }
    if (prompt.toLowerCase().includes('simplify')) {
      return 'I\'ve simplified the language to make it more accessible to all users.';
    }
    if (prompt.toLowerCase().includes('detail')) {
      return 'I\'ve added more detailed explanations and context to help users understand better.';
    }
    if (prompt.toLowerCase().includes('accuracy')) {
      return 'I\'ve verified the technical accuracy and made necessary corrections.';
    }
    return 'I\'ve updated the step based on your feedback. The changes have been applied.';
  };

  const generateStepUpdates = (prompt: string, step: StoryboardStep): Partial<StoryboardStep> | null => {
    if (prompt.toLowerCase().includes('clear')) {
      return {
        onscreenText: `${step.onscreenText} (Clarified)`,
        ttsText: `${step.ttsText} Let me explain this more clearly.`,
        validation: { ...step.validation, clarity: 'excellent' }
      };
    }
    if (prompt.toLowerCase().includes('safety')) {
      return {
        onscreenText: `⚠️ ${step.onscreenText}`,
        ttsText: `Important safety note: ${step.ttsText}`,
        validation: { ...step.validation, safety: 'verified' }
      };
    }
    if (prompt.toLowerCase().includes('simplify')) {
      return {
        onscreenText: step.onscreenText.replace(/\b\w{8,}\b/g, (match) => 
          match.length > 8 ? `${match.substring(0, 6)}...` : match
        ),
        ttsText: `${step.ttsText} (Simplified)`,
        validation: { ...step.validation, clarity: 'good' }
      };
    }
    return null;
  };

  if (!activeStep) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            AI Assistant
          </CardTitle>
          <CardDescription>
            Select a step to start refining with AI assistance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <div className="text-center">
              <Lightbulb className="h-8 w-8 mx-auto mb-2" />
              <p className="text-sm">No step selected</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md h-[600px] flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            AI Assistant
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onUndo}
              disabled={!canUndo}
              className="h-8 w-8 p-0"
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRedo}
              disabled={!canRedo}
              className="h-8 w-8 p-0"
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          Refining: '{activeStep.onscreenText}'
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0">
        {/* Quick Actions */}
        <div className="px-4 pb-3 border-b">
          <div className="grid grid-cols-2 gap-2">
            {predefinedPrompts.map((prompt) => {
              const Icon = prompt.icon;
              return (
                <Button
                  key={prompt.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePredefinedPrompt(prompt.text)}
                  disabled={isProcessing}
                  className={cn(
                    'h-auto p-2 flex flex-col items-center gap-1 text-xs',
                    prompt.color
                  )}
                >
                  <Icon className="h-3 w-3" />
                  <span className="text-center leading-tight">
                    {prompt.text}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4">
          {messages.map((message) => (
            <ChatBubble
              key={message.id}
              message={message}
              isUser={message.role === 'user'}
            />
          ))}
          
          {isProcessing && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-sm">AI is thinking...</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t">
          <form onSubmit={handleInputSubmit} className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask me to refine this step..."
              disabled={isProcessing}
              className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
            <Button
              type="submit"
              disabled={!inputValue.trim() || isProcessing}
              size="sm"
              className="px-3"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};